import { useState, useEffect, useCallback, useRef } from 'react'
import { Plus, Check, Edit2, Trash2, ChevronLeft, ChevronRight, X, RotateCcw, Upload, FileText, Image as ImageIcon, Paperclip, Download, ExternalLink, RefreshCw, Calendar } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

// ── Date helpers ──────────────────────────────────────────────────────────────
const isoToDisplay = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
const isoToDate = (iso) => {
  if (!iso) return null
  const [y, m, d] = iso.split('-')
  return new Date(Number(y), Number(m) - 1, Number(d))
}
const dateToISO = (date) => {
  if (!date) return ''
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }

// ── DateRangePicker ───────────────────────────────────────────────────────────
const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const DAYS_PT   = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb']

function DateRangePicker({ dataInicio, dataFim, onChange }) {
  const today = new Date(); today.setHours(0, 0, 0, 0)

  const [open, setOpen]           = useState(false)
  const [pos, setPos]             = useState({ top: 0, left: 0 })
  const [tempStart, setTempStart] = useState(null)
  const [tempEnd, setTempEnd]     = useState(null)
  const [selecting, setSelecting] = useState('start')
  const [hoverDate, setHoverDate] = useState(null)
  const [months, setMonths]       = useState(() => {
    const m = today.getMonth(), y = today.getFullYear()
    return {
      left:  { year: m === 0 ? y - 1 : y, month: m === 0 ? 11 : m - 1 },
      right: { year: y, month: m },
    }
  })
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        popoverRef.current && !popoverRef.current.contains(e.target)
      ) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const openPicker = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 6, left: r.left })
    }
    setTempStart(isoToDate(dataInicio))
    setTempEnd(isoToDate(dataFim))
    setSelecting('start')
    setOpen(true)
  }

  const shiftMonths = (delta) => setMonths(({ left, right }) => {
    const shift = ({ year, month }) => {
      const m = month + delta
      if (m < 0)  return { year: year - 1, month: 11 }
      if (m > 11) return { year: year + 1, month: 0 }
      return { year, month: m }
    }
    return { left: shift(left), right: shift(right) }
  })

  const handleDayClick = (date) => {
    if (selecting === 'start' || !tempStart || date < tempStart) {
      setTempStart(date); setTempEnd(null); setSelecting('end')
    } else {
      setTempEnd(date); setSelecting('start')
    }
  }

  const applyPreset = (start, end) => { setTempStart(start); setTempEnd(end); setSelecting('start') }

  const handleApply = () => {
    if (!tempStart) return
    onChange(dateToISO(tempStart), dateToISO(tempEnd || tempStart))
    setOpen(false)
  }

  const presets = [
    { label: 'Hoje',           fn: () => applyPreset(today, today) },
    { label: 'Ontem',          fn: () => { const d = addDays(today, -1); applyPreset(d, d) } },
    { label: 'Últimos 7 dias', fn: () => applyPreset(addDays(today, -6), today) },
    { label: 'Últimos 30 dias',fn: () => applyPreset(addDays(today, -29), today) },
    { label: 'Este mês',       fn: () => applyPreset(new Date(today.getFullYear(), today.getMonth(), 1), today) },
    { label: 'Mês passado',    fn: () => applyPreset(
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0)
      )},
    { label: 'Este ano',       fn: () => applyPreset(new Date(today.getFullYear(), 0, 1), today) },
    { label: 'Ano passado',    fn: () => applyPreset(
        new Date(today.getFullYear() - 1, 0, 1),
        new Date(today.getFullYear() - 1, 11, 31)
      )},
  ]

  const renderMonth = (year, month) => {
    const firstDow    = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const cells = []
    for (let i = 0; i < firstDow; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))
    const effectiveEnd = tempEnd || (selecting === 'end' && hoverDate ? hoverDate : null)

    return (
      <div className="w-52">
        <div className="grid grid-cols-7 mb-1">
          {DAYS_PT.map(d => <div key={d} className="text-center text-[11px] text-gray-400 font-medium pb-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((date, i) => {
            if (!date) return <div key={`e${i}`} />
            const iso     = dateToISO(date)
            const isStart = tempStart && dateToISO(tempStart) === iso
            const isEnd   = tempEnd   && dateToISO(tempEnd)   === iso
            const inRange = tempStart && effectiveEnd && date > tempStart && date < effectiveEnd
            const isToday = iso === dateToISO(today)
            return (
              <div key={iso}
                onClick={() => handleDayClick(date)}
                onMouseEnter={() => selecting === 'end' && setHoverDate(date)}
                className={[
                  'text-center text-sm py-[5px] cursor-pointer select-none transition-colors',
                  isStart || isEnd ? 'bg-indigo-600 text-white rounded-full font-semibold' :
                  inRange          ? 'bg-indigo-100 text-indigo-800' :
                  isToday          ? 'text-indigo-600 font-semibold hover:bg-gray-100 rounded-full' :
                                     'text-gray-700 hover:bg-gray-100 rounded-full',
                ].join(' ')}>
                {date.getDate()}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const displayText = dataInicio
    ? `${isoToDisplay(dataInicio)} – ${isoToDisplay(dataFim || dataInicio)}`
    : 'Selecionar período'

  return (
    <div>
      <button ref={triggerRef} onClick={openPicker}
        className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm bg-white transition-colors whitespace-nowrap ${open ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-gray-200 hover:border-gray-300'}`}>
        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className={dataInicio ? 'text-gray-800' : 'text-gray-400'}>{displayText}</span>
      </button>

      {open && (
        <div ref={popoverRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="bg-white rounded-xl shadow-2xl border border-gray-100 p-5 flex gap-0">
          <div className="flex flex-col gap-0.5 pr-4 border-r border-gray-100 mr-5 min-w-[130px]">
            {presets.map(p => (
              <button key={p.label} onClick={p.fn}
                className="text-left text-sm px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 text-gray-600 transition-colors whitespace-nowrap">
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => shiftMonths(-1)} className="p-1 hover:bg-gray-100 rounded text-gray-500">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex gap-8">
                <span className="text-sm font-semibold text-gray-800 w-48 text-center">
                  {MONTHS_PT[months.left.month]} {months.left.year}
                </span>
                <span className="text-sm font-semibold text-gray-800 w-48 text-center">
                  {MONTHS_PT[months.right.month]} {months.right.year}
                </span>
              </div>
              <button onClick={() => shiftMonths(1)} className="p-1 hover:bg-gray-100 rounded text-gray-500">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-5" onMouseLeave={() => setHoverDate(null)}>
              {renderMonth(months.left.year, months.left.month)}
              <div className="w-px bg-gray-100 self-stretch" />
              {renderMonth(months.right.year, months.right.month)}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">
                {tempStart
                  ? `${isoToDisplay(dateToISO(tempStart))} – ${tempEnd ? isoToDisplay(dateToISO(tempEnd)) : '...'}`
                  : 'Selecione o período'}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setOpen(false)}
                  className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                  Cancelar
                </button>
                <button onClick={handleApply} disabled={!tempStart}
                  className="px-4 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-40 font-medium">
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const STATUS_LABEL = {
  em_aberto: { label: 'Em Aberto', cls: 'bg-yellow-100 text-yellow-700' },
  atrasado:  { label: 'Atrasado',  cls: 'bg-red-100 text-red-700' },
  pago:      { label: 'Pago',      cls: 'bg-green-100 text-green-700' },
}

const TIPO_LABEL = {
  comissao: { label: 'Auto',   cls: 'bg-blue-500 text-white' },
  manual:   { label: 'Manual', cls: 'bg-gray-400 text-white' },
}

// ── Upload de Comprovantes ────────────────────────────────────────────────────
function FileUploadArea({ files, onChange }) {
  const inputRef = useRef(null)
  useEffect(() => {
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || [])
      const imageItems = items.filter(i => i.type.startsWith('image/'))
      if (!imageItems.length) return
      const newFiles = imageItems.map((item, idx) => {
        const blob = item.getAsFile()
        return new File([blob], `colado-${Date.now()}-${idx}.png`, { type: 'image/png' })
      })
      onChange(prev => [...prev, ...newFiles])
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [onChange])
  const handleDrop = (e) => {
    e.preventDefault()
    const added = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/') || f.type === 'application/pdf')
    if (added.length) onChange(prev => [...prev, ...added])
  }
  const handleInput = (e) => {
    const added = Array.from(e.target.files || [])
    if (added.length) onChange(prev => [...prev, ...added])
    e.target.value = ''
  }
  const remove = (i) => onChange(prev => prev.filter((_, idx) => idx !== i))
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-600">Comprovantes (opcional)</label>
      <div onDrop={handleDrop} onDragOver={e => e.preventDefault()} onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
        <Upload className="w-4 h-4 text-gray-300 mx-auto mb-1" />
        <p className="text-xs text-gray-400">Arraste, clique ou <kbd className="font-mono text-[10px] bg-gray-100 border border-gray-200 rounded px-1">Ctrl+V</kbd> para colar</p>
        <p className="text-[11px] text-gray-300 mt-0.5">PDF, PNG, JPG</p>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" onChange={handleInput} className="hidden" />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 text-xs max-w-[180px]">
              {f.type === 'application/pdf' ? <FileText className="w-3 h-3 text-red-500 flex-shrink-0" /> : <ImageIcon className="w-3 h-3 text-blue-500 flex-shrink-0" />}
              <span className="truncate text-gray-700">{f.name}</span>
              <button onClick={(e) => { e.stopPropagation(); remove(i) }} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Modal Comprovantes ────────────────────────────────────────────────────────
function ModalComprovantes({ conta, orgId, onClose }) {
  const [comprovantes, setComprovantes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    gestaoService.listarComprovantesContaPagar(orgId, conta.id)
      .then(r => setComprovantes(r.data || []))
      .catch(() => toast.error('Erro ao carregar comprovantes'))
      .finally(() => setLoading(false))
  }, [orgId, conta.id])

  const handleDownload = async (comp) => {
    try {
      const a = document.createElement('a')
      a.href = comp.url
      a.download = comp.nome_arquivo
      a.target = '_blank'
      a.click()
    } catch {
      toast.error('Erro ao baixar comprovante')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Comprovantes — {conta.descricao}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 min-h-[120px]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comprovantes.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum comprovante anexado</p>
          ) : (
            <div className="space-y-3">
              {comprovantes.map(comp => (
                <div key={comp.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  {comp.tipo_arquivo?.startsWith('image/') ? (
                    <img src={comp.url} alt={comp.nome_arquivo} className="w-12 h-12 object-cover rounded-md border border-gray-200 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-md border border-red-100 flex-shrink-0">
                      <FileText className="w-6 h-6 text-red-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{comp.nome_arquivo}</p>
                    <p className="text-xs text-gray-400">{new Date(comp.created_at).toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <a href={comp.url} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-md" title="Visualizar">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button onClick={() => handleDownload(comp)}
                      className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md" title="Baixar">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Baixar ──────────────────────────────────────────────────────────────
function ModalBaixar({ conta, formas, onClose, onConfirm }) {
  const [valorPago, setValorPago] = useState(
    Number(conta.valor - (conta.valor_pago || 0)).toFixed(2)
  )
  const [dataBaixa, setDataBaixa] = useState(new Date().toISOString().split('T')[0])
  const [formaPagamentoId, setFormaPagamentoId] = useState(
    formas.find(f => f.is_padrao)?.id || formas[0]?.id || ''
  )
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    if (!formaPagamentoId) return toast.error('Selecione a forma de pagamento')
    setLoading(true)
    await onConfirm({ valor_pago: Number(valorPago), data_baixa: dataBaixa, forma_pagamento_id: Number(formaPagamentoId) }, files)
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">Baixar Despesa do Leilão</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1 text-sm">
            <p><span className="font-medium">Descrição:</span> {conta.descricao}</p>
            <p><span className="font-medium">Valor:</span> {fmt(conta.valor)}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Valor Pago</label>
            <input type="number" step="0.01" value={valorPago} onChange={e => setValorPago(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Data do Pagamento</label>
            <input type="date" value={dataBaixa} onChange={e => setDataBaixa(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Forma de Pagamento</label>
            <select value={formaPagamentoId} onChange={e => setFormaPagamentoId(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              {formas.filter(f => f.is_ativo).map(f => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>
          <FileUploadArea files={files} onChange={setFiles} />
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleConfirm} disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Salvando...' : 'Confirmar Baixa'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Modal Nova / Editar Conta ─────────────────────────────────────────────────
function ModalFormConta({ leiloes, categorias, editando, onClose, onSalvar }) {
  const [form, setForm] = useState({
    descricao: editando?.descricao || '',
    valor: editando?.valor || '',
    data_vencimento: editando?.data_vencimento || '',
    leilao_id: editando?.leilao_id || '',
    categoria_id: editando?.categoria_id || '',
    recorrente: editando?.recorrente || false,
    observacoes: editando?.observacoes || '',
  })
  const [loading, setLoading] = useState(false)
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleSalvar = async () => {
    if (!form.descricao || !form.valor || !form.data_vencimento)
      return toast.error('Preencha descrição, valor e data de vencimento')
    setLoading(true)
    await onSalvar({
      ...form,
      valor: Number(form.valor),
      leilao_id: form.leilao_id || null,
      categoria_id: form.categoria_id || null,
    })
    setLoading(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{editando ? 'Editar' : 'Nova'} Conta a Pagar</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Descrição *</label>
            <input value={form.descricao} onChange={e => set('descricao', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Ex: Taxa IMA, Comissão leiloeiro..." />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Valor *</label>
            <input type="number" step="0.01" value={form.valor} onChange={e => set('valor', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="0,00" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Vencimento *</label>
            <input type="date" value={form.data_vencimento} onChange={e => set('data_vencimento', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Leilão</label>
            <select value={form.leilao_id} onChange={e => set('leilao_id', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Nenhum</option>
              {leiloes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Categoria</label>
            <select value={form.categoria_id} onChange={e => set('categoria_id', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Nenhuma</option>
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="recorrente" checked={form.recorrente} onChange={e => set('recorrente', e.target.checked)}
              className="w-4 h-4 rounded" />
            <label htmlFor="recorrente" className="text-sm text-gray-600">Recorrente</label>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
            <textarea value={form.observacoes} onChange={e => set('observacoes', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" rows={2} />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function ContasPagar() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [contas, setContas] = useState([])
  const [leiloes, setLeiloes] = useState([])
  const [formas, setFormas] = useState([])
  const [categorias, setCategorias] = useState([])
  const [loading, setLoading] = useState(false)

  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [leilaoId, setLeilaoId] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [busca, setBusca] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('')
  const [categoriaFiltro, setCategoriaFiltro] = useState('')

  const [modalBaixar, setModalBaixar] = useState(null)
  const [modalForm, setModalForm] = useState(null) // null | 'nova' | {conta}
  const [modalComprov, setModalComprov] = useState(null)
  const [confirmExcluir, setConfirmExcluir] = useState(null)
  const [scopeEdicao, setScopeEdicao] = useState(null) // { dados, conta }
  const [syncing, setSyncing] = useState(false)

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      if (busca) filtros.busca = busca
      if (statusFiltro) filtros.status = statusFiltro
      if (categoriaFiltro) filtros.categoria_id = categoriaFiltro
      const res = await gestaoService.listarContasPagar(orgId, filtros)
      setContas(res.data || [])
    } catch {
      toast.error('Erro ao carregar contas')
    } finally {
      setLoading(false)
    }
  }, [orgId, modoPeriodo, leilaoId, dataInicio, dataFim, busca, statusFiltro, categoriaFiltro])

  useEffect(() => {
    if (!orgId) return
    Promise.all([
      leiloesService.getByOrganizador(orgId),
      gestaoService.listarFormasPagamento(orgId),
      gestaoService.listarCategorias(orgId, 'despesa'),
    ]).then(async ([ls, fps, cats]) => {
      const lista = ls || []
      setLeiloes(lista)
      setFormas(fps.data || [])
      setCategorias(cats.data || [])
      // Seleciona o leilão mais recente (o useEffect de leilaoId vai sincronizar e carregar)
      if (lista.length > 0 && !leilaoId) {
        const maisRecente = lista[0]
        setLeilaoId(String(maisRecente.id))
      }
    }).catch(() => {})
  }, [orgId])

  // Sincroniza e recarrega ao trocar de leilão
  useEffect(() => {
    if (!orgId || !leilaoId) return
    const syncAndLoad = async () => {
      try {
        await gestaoService.sincronizar(orgId, leilaoId)
      } catch { /* ignora */ }
      carregar()
    }
    syncAndLoad()
  }, [leilaoId])

  // Auto-filtra quando busca ou status muda
  useEffect(() => {
    if (orgId && leilaoId) carregar()
  }, [busca, statusFiltro, categoriaFiltro, modoPeriodo, dataInicio, dataFim])

  const handleSincronizar = async () => {
    if (!leilaoId) return
    setSyncing(true)
    try {
      const res = await gestaoService.sincronizar(orgId, leilaoId)
      const d = res.data || res
      toast.success(`Sincronizado: ${d.contas_geradas ?? 0} novas, ${d.contas_atualizadas ?? 0} atualizadas`)
      carregar()
    } catch {
      toast.error('Erro ao sincronizar')
    } finally {
      setSyncing(false)
    }
  }

  const handleBaixar = async (dados, files = []) => {
    try {
      await gestaoService.baixarContaPagar(orgId, modalBaixar.id, dados)
      if (files.length) {
        await Promise.allSettled(
          files.map(f => gestaoService.uploadComprovanteContaPagar(orgId, modalBaixar.id, f))
        )
      }
      toast.success('Baixa registrada!')
      setModalBaixar(null)
      carregar()
    } catch {
      toast.error('Erro ao registrar baixa')
    }
  }

  const handleSalvar = async (dados) => {
    const editando = modalForm && typeof modalForm === 'object' && modalForm.id ? modalForm : null
    if (editando && editando.serie_id) {
      // É recorrente com série — pede escopo antes de salvar
      setScopeEdicao({ dados, conta: editando })
      setModalForm(null)
      return
    }
    try {
      if (editando) {
        await gestaoService.atualizarContaPagar(orgId, editando.id, dados)
        toast.success('Conta atualizada!')
      } else {
        await gestaoService.criarContaPagar(orgId, dados)
        toast.success('Conta criada!')
      }
      setModalForm(null)
      carregar()
    } catch {
      toast.error('Erro ao salvar')
    }
  }

  const confirmarScopeEdicao = async (scope) => {
    const { dados, conta } = scopeEdicao
    setScopeEdicao(null)
    try {
      await gestaoService.atualizarContaPagar(orgId, conta.id, dados, scope)
      toast.success(scope === 'future' ? 'Esta e as próximas atualizadas!' : 'Conta atualizada!')
      carregar()
    } catch {
      toast.error('Erro ao salvar')
    }
  }

  const [confirmEstorno, setConfirmEstorno] = useState(null)

  const handleEstornar = async (conta) => {
    setConfirmEstorno(conta)
  }

  const confirmarEstorno = async () => {
    const conta = confirmEstorno
    setConfirmEstorno(null)
    try {
      await gestaoService.estornarContaPagar(orgId, conta.id)
      toast.success('Baixa estornada!')
      carregar()
    } catch {
      toast.error('Erro ao estornar')
    }
  }

  const handleExcluir = (conta) => setConfirmExcluir(conta)

  const confirmarExcluir = async (scope = 'only') => {
    try {
      await gestaoService.excluirContaPagar(orgId, confirmExcluir.id, scope)
      toast.success(scope === 'future' ? 'Esta e as próximas excluídas!' : 'Conta excluída!')
      setConfirmExcluir(null)
      carregar()
    } catch {
      toast.error('Erro ao excluir')
    }
  }

  const totais = contas.reduce(
    (acc, c) => {
      acc.total += Number(c.valor)
      acc.pago += Number(c.valor_pago || 0)
      if (c.status === 'em_aberto') acc.em_aberto += Number(c.valor)
      if (c.status === 'atrasado') acc.atrasado += Number(c.valor)
      return acc
    },
    { total: 0, pago: 0, em_aberto: 0, atrasado: 0 }
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-gray-800">Despesas do Leilão</h1>
        <div className="flex gap-2">
          {leilaoId && !modoPeriodo && (
            <button onClick={handleSincronizar} disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium">
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </button>
          )}
          <button onClick={() => setModalForm('nova')}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" />
            Nova Conta
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 overflow-x-auto">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            <button onClick={() => setModoPeriodo(false)}
              className={`px-3 py-2 text-sm font-medium transition-colors ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Leilão
            </button>
            <button onClick={() => setModoPeriodo(true)}
              className={`px-3 py-2 text-sm font-medium transition-colors ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Período
            </button>
          </div>

          {!modoPeriodo ? (
            <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-52">
              <option value="">Todos os leilões</option>
              {leiloes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          ) : (
            <DateRangePicker
              dataInicio={dataInicio}
              dataFim={dataFim}
              onChange={(s, e) => { setDataInicio(s); setDataFim(e) }}
            />
          )}

          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar descrição..."
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-44" />

          <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
            <option value="">Todos os status</option>
            <option value="em_aberto">Em Aberto</option>
            <option value="atrasado">Atrasado</option>
            <option value="pago">Pago</option>
          </select>

          <select value={categoriaFiltro} onChange={e => setCategoriaFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[150px]">
            <option value="">Todas as categorias</option>
            {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Vencimento','Tipo','Descrição','Leilão','Categoria','Valor a Pagar','Valor Pago','Data Baixa','Status','Recorrente','Ações'].map(h => (
                      <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contas.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-12 text-center text-gray-400 text-sm">
                        Nenhuma conta encontrada
                      </td>
                    </tr>
                  ) : contas.map(c => {
                    const status = STATUS_LABEL[c.status] || STATUS_LABEL.em_aberto
                    const tipo = TIPO_LABEL[c.tipo_lancamento] || TIPO_LABEL.manual
                    return (
                      <tr key={c.id} className="hover:bg-gray-50 border-b border-gray-100">
                        <td className="px-4 py-3 text-sm text-gray-700">{fmtDate(c.data_vencimento)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${tipo.cls}`}>{tipo.label}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[220px] truncate" title={c.descricao}>{c.descricao}</td>
                        <td className="px-4 py-3 text-sm text-blue-600 max-w-[180px] truncate">{c.leilao?.nome || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{c.categoria?.nome || '—'}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-800">{fmt(c.valor)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{fmt(c.valor_pago)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(c.data_baixa)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 rounded-md text-xs font-semibold ${status.cls}`}>{status.label}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{c.recorrente ? 'Sim' : '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {c.status !== 'pago' && (
                              <button onClick={() => setModalBaixar(c)} title="Baixar"
                                className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md">
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {c.status === 'pago' && (
                              <button onClick={() => handleEstornar(c)} title="Estornar"
                                className="p-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md">
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {c.status === 'pago' && (
                              <button onClick={() => setModalComprov(c)} title="Comprovantes"
                                className="p-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-md">
                                <Paperclip className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {c.tipo_lancamento !== 'comissao' && (
                              <button onClick={() => setModalForm(c)} title="Editar"
                                className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {c.tipo_lancamento !== 'comissao' && (
                              <button onClick={() => handleExcluir(c)} title="Excluir"
                                className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-md">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Totais */}
            {contas.length > 0 && (
              <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 flex flex-wrap gap-6 text-sm">
                <span className="text-gray-600">Total: <strong className="text-gray-800">{fmt(totais.total)}</strong></span>
                <span className="text-yellow-700">Em Aberto: <strong>{fmt(totais.em_aberto)}</strong></span>
                <span className="text-red-600">Atrasado: <strong>{fmt(totais.atrasado)}</strong></span>
                <span className="text-green-700">Pago: <strong>{fmt(totais.pago)}</strong></span>
                <span className="text-gray-500 ml-auto">Ainda a pagar: <strong className="text-gray-800">{fmt(totais.total - totais.pago)}</strong></span>
              </div>
            )}
          </>
        )}
      </div>

      {modalBaixar && (
        <ModalBaixar conta={modalBaixar} formas={formas}
          onClose={() => setModalBaixar(null)} onConfirm={handleBaixar} />
      )}
      {modalForm && (
        <ModalFormConta
          leiloes={leiloes}
          categorias={categorias}
          editando={typeof modalForm === 'object' && modalForm.id ? modalForm : null}
          onClose={() => setModalForm(null)}
          onSalvar={handleSalvar}
        />
      )}
      {modalComprov && (
        <ModalComprovantes conta={modalComprov} orgId={orgId} onClose={() => setModalComprov(null)} />
      )}

      {/* Modal Escopo de Edição (série) */}
      {scopeEdicao && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-1 text-center">Despesa recorrente</h3>
            <p className="text-gray-500 text-sm text-center mb-5">Como deseja salvar as alterações?</p>
            <div className="flex flex-col gap-3 mb-5">
              <button onClick={() => confirmarScopeEdicao('only')}
                className="w-full text-left px-4 py-3 border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-xl transition group">
                <div className="font-medium text-gray-800 group-hover:text-blue-700 text-sm">Só esta ocorrência</div>
                <div className="text-xs text-gray-500 mt-0.5">As outras da série não serão afetadas</div>
              </button>
              <button onClick={() => confirmarScopeEdicao('future')}
                className="w-full text-left px-4 py-3 border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 rounded-xl transition group">
                <div className="font-medium text-gray-800 group-hover:text-blue-700 text-sm">Esta e as próximas</div>
                <div className="text-xs text-gray-500 mt-0.5">Atualiza todas as ocorrências não pagas a partir desta</div>
              </button>
            </div>
            <button onClick={() => setScopeEdicao(null)} className="w-full border border-gray-300 text-gray-600 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal Confirmação Exclusão */}
      {confirmExcluir && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2 text-center">Excluir despesa?</h3>
            <p className="text-gray-600 text-sm text-center mb-2">{confirmExcluir.descricao}</p>
            <p className="text-center font-semibold text-lg mb-1">
              {Number(confirmExcluir.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
            {confirmExcluir.serie_id ? (
              <>
                <p className="text-xs text-gray-500 text-center mb-4">Esta é uma despesa recorrente. O que deseja excluir?</p>
                <div className="flex flex-col gap-2 mb-4">
                  <button onClick={() => confirmarExcluir('only')}
                    className="w-full text-left px-4 py-3 border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 rounded-xl transition group">
                    <div className="font-medium text-gray-800 group-hover:text-red-700 text-sm">Só esta ocorrência</div>
                    <div className="text-xs text-gray-500 mt-0.5">As outras da série continuam</div>
                  </button>
                  <button onClick={() => confirmarExcluir('future')}
                    className="w-full text-left px-4 py-3 border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 rounded-xl transition group">
                    <div className="font-medium text-gray-800 group-hover:text-red-700 text-sm">Esta e as próximas</div>
                    <div className="text-xs text-gray-500 mt-0.5">Cancela a série a partir deste ponto</div>
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-red-600 text-center mb-5">Esta ação não pode ser desfeita.</p>
                <button onClick={() => confirmarExcluir('only')} className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-medium text-sm transition mb-3">
                  Excluir
                </button>
              </>
            )}
            <button onClick={() => setConfirmExcluir(null)} className="w-full border border-gray-300 text-gray-600 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal Confirmação Estorno */}
      {confirmEstorno && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2 text-center">Estornar baixa?</h3>
            <p className="text-gray-600 text-sm text-center mb-2">{confirmEstorno.descricao}</p>
            <p className="text-center font-semibold text-lg mb-1">
              {Number(confirmEstorno.valor_pago || confirmEstorno.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
            <p className="text-xs text-red-600 text-center mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmEstorno(null)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium text-sm hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={confirmarEstorno} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-2.5 rounded-lg font-medium text-sm transition">
                Estornar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
